const API_BASE = '/api/v1/user/settings';

export interface UserSettings {
  llm_provider?: string;
  llm_model?: string;
  ollama_base_url?: string;
  anthropic_api_key?: string;
  openai_api_key?: string;
  auto_route_images?: string;
  auto_route_documents?: string;
  strava_client_id?: string;
  strava_client_secret?: string;
}

export interface EffectiveSettings {
  llm_provider: string;
  llm_model: string;
  ollama_base_url?: string;
  has_anthropic_key: boolean;
  has_openai_key: boolean;
  auto_route_images: boolean;
  auto_route_documents: boolean;
  has_strava_credentials: boolean;
}

export interface OllamaModel {
  name: string;
  size: string;
}

export interface LLMStatus {
  status: 'connected' | 'disconnected' | 'configured' | 'not_configured' | 'unknown';
  provider: string;
}

export interface StravaStatus {
  hasCredentials: boolean;
  isConnected: boolean;
  isExpired: boolean | null;
  athleteId: string | null;
}

async function handleResponse<T>(response: Response): Promise<T> {
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || 'Errore sconosciuto');
  }
  return data;
}

export async function getUserSettings(): Promise<{
  success: boolean;
  settings: UserSettings;
  effective: EffectiveSettings;
}> {
  const response = await fetch(API_BASE, {
    credentials: 'include',
  });
  return handleResponse(response);
}

export async function updateUserSettings(
  settings: Partial<UserSettings>
): Promise<{ success: boolean; message?: string }> {
  const response = await fetch(API_BASE, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(settings),
  });
  return handleResponse(response);
}

export async function resetUserSettings(): Promise<{
  success: boolean;
  message?: string;
}> {
  const response = await fetch(API_BASE, {
    method: 'DELETE',
    credentials: 'include',
  });
  return handleResponse(response);
}

export async function getUserOllamaModels(): Promise<{
  success: boolean;
  models?: OllamaModel[];
  error?: string;
}> {
  const response = await fetch(`${API_BASE}/ollama/models`, {
    credentials: 'include',
  });
  return handleResponse(response);
}

export async function getUserLLMStatus(): Promise<{
  success: boolean;
  status: LLMStatus['status'];
  provider: string;
}> {
  const response = await fetch(`${API_BASE}/llm/status`, {
    credentials: 'include',
  });
  return handleResponse(response);
}

export async function getStravaStatus(): Promise<{
  success: boolean;
  status: StravaStatus;
}> {
  const response = await fetch(`${API_BASE}/strava/status`, {
    credentials: 'include',
  });
  return handleResponse(response);
}

export interface VisionStatus {
  provider: string;
  model: string;
  supportsVision: boolean;
  canAutoRoute: boolean;
  willWork: boolean;
  reason: string;
  suggestions: string[];
}

export async function getVisionStatus(): Promise<{
  success: boolean;
} & VisionStatus> {
  const response = await fetch(`${API_BASE}/vision/status`, {
    credentials: 'include',
  });
  return handleResponse(response);
}
